import { UnshadowedElement, html } from "../lib/unshadowed.js";
import { SocialEmailLink, SocialFacebookLink } from "../lib/social-links.js";
import { TrackClick } from "../lib/click-tracking.js";

/**
 * Buttons for share modal
 */
export class ShareOptions extends UnshadowedElement {
    static elementName() { return "share-options" }

    static properties = {
        name: { type: String },
        url: { type: String },
        hideFlyer: { type: Boolean },
        hideInvite: { type: Boolean },
        itemType: { type: String },
    };

    constructor() {
        super();
        // Defaults
        this.hideFlyer = false;
        this.hideInvite = false;
    }

    /** Trigger the native share feature, if available */
    async ShareNative() {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: "Meal Train for " + this.name,
                    text: "Meal Train for " + this.name + "\n",
                    url: window.CurrentTrain.Url,
                });
            } catch (err) {
                console.log('Error sharing:', err);
            }
        } else {
            console.log("Browser does not support native share");
        }
    }

    /** Copy the URL to the clipboard, and show a "copied" message */
    async CopyToClipboard(e, url) {
        await navigator.clipboard.writeText(url);
        if (e && e.target) {
            if (!e.target.classList.contains("copied")) {
                e.target.classList.add("copied");
                setTimeout(() => {
                    e.target.classList.remove("copied");
                }, 3000);
            }
        }
    }

    render() {
        const fullName = "Meal Train for " + this.name;
        let flyerLink = null;
        let invitationLink = null;
        if (!this.hideFlyer) {
            flyerLink = html`
                <a id="btn-share-printflyer" @click=${e => { TrackClick(e.currentTarget); }} href="${window.CurrentTrain.OpenUrl}/flyer" target="_blank" class="button button-clear button-rectangle">
                    <svg role="img" width="30" height="30" aria-label="Print a flyer for this page">
                        <use xlink:href="#print-blue" />
                    </svg>
                    Print Flyer
                </a>`;
        }
        if (!this.hideInvite) {
            invitationLink = html`
                <div class="invitation-tool">
                    <h3 class="font:label-s space:stack:space-xs">Add</h3>
                    <p class="space:stack:space-s">Import from past Meal Trains or add emails to the participant list.</p>
                    <a id="btn-share-invitations" @click=${e => { TrackClick(e.currentTarget); }} href="/trains/${window.CurrentTrain.Id}/invite/" class="button button-condensed button-rectangle button-outline"><svg role="img" width="24" height="24" aria-label="Add">                 
                    <use xlink:href="#recipient-count-2"></use></svg>Add
                    </a>          
                </div>`;
        }
        return html`
            <div class="train-share-options">
                <a id="btn-share-facebook" @click=${e => { TrackClick(e.currentTarget); }} href="${SocialFacebookLink(this.url)}" target="_blank" class="button button-clear button-rectangle">
                    <svg role="img" width="24" height="21" aria-label="Share on Facebook">
                        <use xlink:href="#facebook-logo-color" />
                    </svg>
                    <span>Share to Facebook</span>
                </a>
                <a id="btn-share-email" @click=${e => { TrackClick(e.currentTarget); }} href="${SocialEmailLink(fullName, this.url)}" target="_blank" class="button button-clear button-rectangle">
                    <svg role="img" width="30" height="30" aria-label="Email this page">
                        <use xlink:href="#envelope-blue-2" />
                    </svg>
                    Email
                </a>
                <button id="btn-share-message" @click=${e => { this.ShareNative(); TrackClick(e.currentTarget); }} class="native-share-visible hide-native-share-over-1000 button button-clear button-rectangle">
                    <svg role="img" width="30" height="30" aria-label="Send this page as a message">
                        <use xlink:href="#message-blue-2" />
                    </svg>
                    Message
                </button>
                ${flyerLink}
                <button id="btn-share-moreoptions" @click=${e => { this.ShareNative(); TrackClick(e.currentTarget); }} class="native-share-visible hide-native-share-over-1000 button button-clear button-rectangle">
                    <svg class="hide-platform-android" role="img" width="30" height="30" aria-label="Share with your OS">
                        <use xlink:href="#share-options-ios-blue" />
                    </svg>
                    <svg class="show-platform-android" role="img" width="30" height="30" aria-label="Share with your OS">
                        <use xlink:href="#share-options-android-blue" />
                    </svg>
                    Other:
                    <svg class="share-social-icon" role="img" width="30" height="30" aria-label="Share on Instagram">
                        <use xlink:href="#instagram" />
                    </svg>
                    <svg class="share-social-icon" role="img" width="30" height="30" aria-label="Share on WhatsApp">
                        <use xlink:href="#whatsapp" />
                    </svg>
                    <svg class="share-social-icon" role="img" width="30" height="30" aria-label="Share on Facebook Messenger">
                        <use xlink:href="#f-messenger" />
                    </svg>
                    <svg class="share-social-icon" role="img" width="30" height="30" aria-label="Share with your OS">
                        <use xlink:href="#share-fill" />
                    </svg>
                    <svg class="share-social-icon" role="img" width="30" height="30" aria-label="Share on X, formerly known as Twitter">
                        <use xlink:href="#twitter-x" />
                    </svg>
                </button>
                <div class="copy-to-link-box">
                    <p>Here is the link to this ${this.itemType || window.CurrentTrain.Type}:
                    </p>
                    <div class="row">
                        <div id="lnk-share-copylink" @click=${e => { this.CopyToClipboard(e, this.url); TrackClick(e.currentTarget); }} class="strong copy-text-link">${this.url}</div>
                        <button id="btn-share-copylink" @click=${e => { this.CopyToClipboard(e, this.url); TrackClick(e.currentTarget); }} class="button button-blue button-rectangle button-condensed copy-text-button">
                            <svg role="img" width="30" height="30" aria-label="Copy to clipboard">
                                <use xlink:href="#copy-to-clipboard-white" />
                            </svg>
                            <span>Copy</span>
                            <span class="success-text">Copied</span>
                        </button>
                    </div>
                </div>
                ${invitationLink}
            </div>`;
    }
}
